import React from 'react';
import type { Exercise } from '../types';

interface ExerciseCardProps {
  exercise: Exercise;
  onSelect?: (exercise: Exercise) => void;
  isSelected?: boolean;
  showDetails?: boolean;
  className?: string;
}

/**
 * ExerciseCard component for displaying individual exercises
 * Supports selection, detailed view, and Russian text display
 */
export const ExerciseCard: React.FC<ExerciseCardProps> = ({
  exercise,
  onSelect,
  isSelected = false,
  showDetails = false,
  className = ''
}) => {
  const handleClick = () => {
    if (onSelect) {
      onSelect(exercise);
    }
  };

  const baseClasses = `
    bg-white rounded-lg border-2 p-4 transition-all duration-200 cursor-pointer
    hover:shadow-md hover:border-blue-300
    ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}
    ${className}
  `;

  return (
    <div 
      className={baseClasses}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {/* Exercise Name */}
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-lg font-semibold text-gray-900 leading-tight">
          {exercise.name_ru}
        </h3>
        {isSelected && (
          <div className="flex-shrink-0 ml-2">
            <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        )}
      </div>

      {/* English Name (if different) */}
      {exercise.name_en !== exercise.name_ru && (
        <p className="text-sm text-gray-600 mb-2 italic">
          {exercise.name_en}
        </p>
      )}

      {/* Primary and Secondary Muscles, Equipment */}
      <div className="flex flex-wrap gap-2 mb-3">
        {/* Primary Muscles */}
        {exercise.primary_muscles.map(muscle => (
          <span key={muscle} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {muscle}
          </span>
        ))}
        
        {/* Secondary Muscles */}
        {exercise.secondary_muscles.map(muscle => (
          <span key={muscle} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600">
            {muscle}
          </span>
        ))}
        
        {/* Equipment */}
        {exercise.equipment.map((eq, index) => (
          <span key={`${eq}-${index}`} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {eq}
          </span>
        ))}
      </div>

      {/* Exercise Type and Difficulty */}
      <div className="flex flex-wrap gap-2 mb-3">
        {exercise.exercise_type && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            {exercise.exercise_type.replace('_', ' ')}
          </span>
        )}
        {exercise.difficulty_level && (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            exercise.difficulty_level === 'начинающий' ? 'bg-green-100 text-green-800' :
            exercise.difficulty_level === 'средний' ? 'bg-yellow-100 text-yellow-800' :
            'bg-red-100 text-red-800'
          }`}>
            {exercise.difficulty_level}
          </span>
        )}
      </div>

      {/* Description (if showing details) */}
      {showDetails && exercise.description && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-sm text-gray-700 leading-relaxed">
            {exercise.description}
          </p>
        </div>
      )}

      {/* Tags (if showing details) */}
      {showDetails && exercise.tags && exercise.tags.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="flex flex-wrap gap-1">
            {exercise.tags.map(tag => (
              <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-50 text-gray-600">
                #{tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Selection indicator for accessibility */}
      {isSelected && (
        <div className="sr-only">
          Выбрано
        </div>
      )}
    </div>
  );
};

export default ExerciseCard;